package com.schoolconnect.app.feature.schoolusers

import com.schoolconnect.app.datasource.network.response.SchoolUsersAnalyticsResponse
import com.schoolconnect.app.feature.common.ServerPagedListUiState
import com.schoolconnect.app.feature.common.ServerPagingState
import com.schoolconnect.app.feature.schoolusers.add.AddSchoolUserFormValues
import com.schoolconnect.app.feature.schoolusers.add.AddSchoolUserSchoolOption
import com.schoolconnect.app.feature.schoolusers.edit.EditSchoolUserFormValues

data class SchoolUsersFilterFormValues(
    val searchQuery: String = "",
    val schoolId: String = "",
    val schoolName: String = "",
    val role: String = "",
    val status: String = "",
    val createdFrom: String = "",
    val createdTo: String = ""
)

enum class SchoolUsersStatusFilter {
    ALL,
    ACTIVE,
    INACTIVE
}

internal fun SchoolUsersStatusFilter.displayLabel(): String = when (this) {
    SchoolUsersStatusFilter.ALL -> "All"
    SchoolUsersStatusFilter.ACTIVE -> "Active"
    SchoolUsersStatusFilter.INACTIVE -> "Inactive"
}

internal fun String.toSchoolUsersStatusFilter(): SchoolUsersStatusFilter = when (trim().lowercase()) {
    "active" -> SchoolUsersStatusFilter.ACTIVE
    "inactive", "suspended" -> SchoolUsersStatusFilter.INACTIVE
    else -> SchoolUsersStatusFilter.ALL
}

enum class SchoolUsersSortField(val apiField: String) {
    USER("name"),
    SCHOOL("school_name"),
    MOBILE("mobile"),
    EMAIL("email"),
    ROLE("role_id"),
    STATUS("is_active")
}

enum class SchoolUserRoleUi {
    SCHOOL_ADMIN,
    SCHOOL_MANAGEMENT
}

enum class SchoolUserStatusUi {
    ACTIVE,
    INACTIVE
}

data class SchoolUserRowUi(
    val id: String,
    val name: String,
    val school: String,
    val schoolId: String = "",
    val assignmentId: String = "",
    val staffId: String = "",
    val staffRole: String = "",
    val gender: String = "",
    val city: String,
    val mobile: String,
    val email: String,
    val role: SchoolUserRoleUi,
    val status: SchoolUserStatusUi,
    val createdOn: String = "",
    val updatedOn: String = "",
    val createdBy: String = "",
    val updatedBy: String = ""
)

data class SchoolUsersUiState(
    override val paging: ServerPagingState = ServerPagingState(),
    val isLoading: Boolean = false,
    val isAnalyticsLoading: Boolean = false,
    val errorMessage: String = "",
    val infoMessage: String = "",
    val isFilterModalOpen: Boolean = false,
    val isAddUserModalOpen: Boolean = false,
    val isAddUserSaving: Boolean = false,
    val isEditUserModalOpen: Boolean = false,
    val editingUserId: String = "",
    val isEditUserSaving: Boolean = false,
    val searchQuery: String = "",
    val statusFilter: SchoolUsersStatusFilter = SchoolUsersStatusFilter.ALL,
    val schoolFilterId: String = "",
    val schoolFilterName: String = "",
    val roleFilter: String = "",
    val createdFrom: String = "",
    val createdTo: String = "",
    val sortField: SchoolUsersSortField = SchoolUsersSortField.SCHOOL,
    val sortAscending: Boolean = true,
    val isViewUserModalOpen: Boolean = false,
    val viewingUserId: String = "",
    val users: List<SchoolUserRowUi> = emptyList(),
    val schoolOptions: List<AddSchoolUserSchoolOption> = emptyList(),
    val analytics: SchoolUsersAnalyticsResponse? = null
) : ServerPagedListUiState

sealed interface SchoolUsersUiEvent {
    data object BackClicked : SchoolUsersUiEvent
    data object RefreshRequested : SchoolUsersUiEvent
    data class SearchChanged(val query: String) : SchoolUsersUiEvent
    data class StatusFilterChanged(val statusFilter: SchoolUsersStatusFilter) : SchoolUsersUiEvent
    data class SchoolFilterChanged(val schoolId: String, val schoolName: String = "") : SchoolUsersUiEvent
    data class RoleFilterChanged(val role: String) : SchoolUsersUiEvent
    data class CreatedFromChanged(val createdFrom: String) : SchoolUsersUiEvent
    data class CreatedToChanged(val createdTo: String) : SchoolUsersUiEvent
    data object FilterModalOpened : SchoolUsersUiEvent
    data object FilterModalClosed : SchoolUsersUiEvent
    data object ToolbarReset : SchoolUsersUiEvent
    data object FiltersReset : SchoolUsersUiEvent
    data class SortChanged(val field: SchoolUsersSortField) : SchoolUsersUiEvent
    data class PageChanged(val pageIndex: Int) : SchoolUsersUiEvent
    data class PageSizeChanged(val pageSize: Int) : SchoolUsersUiEvent
    data class ToggleStatus(val userId: String) : SchoolUsersUiEvent
    data class DeleteUser(val userId: String) : SchoolUsersUiEvent
    data class ViewDetails(val userId: String) : SchoolUsersUiEvent
    data object ViewUserModalClosed : SchoolUsersUiEvent
    data class EditUser(val userId: String) : SchoolUsersUiEvent
    data object EditUserModalClosed : SchoolUsersUiEvent
    data class EditUserSubmitted(val userId: String, val values: EditSchoolUserFormValues) : SchoolUsersUiEvent
    data object AddUserClicked : SchoolUsersUiEvent
    data object AddUserModalClosed : SchoolUsersUiEvent
    data class AddUserSubmitted(val values: AddSchoolUserFormValues) : SchoolUsersUiEvent
    data object ClearMessages : SchoolUsersUiEvent
}
